import { exportToCSV, exportToPDF } from '../../../utils/exportUtils';
import React, {useState, useRef} from 'react';
import AppCard from '../../../components/ui/AppCard';
import {DataTable} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {Tag} from 'primereact/tag';
import {Button} from 'primereact/button';
import {Dialog} from 'primereact/dialog';
import {InputTextarea} from 'primereact/inputtextarea';
import {Toast} from 'primereact/toast';
import {useMountEffect} from 'primereact/hooks';
import {getAllClaims, approveOrRejectClaim} from '../../../services/claims-service';
import Header from '../../../components/layout/headers/Header';
import DynamicNavigation from "../../../components/layout/navigation/DynamicNavigation";

const AdminClaimsList = () => {
    const [claims, setClaims] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const dt = useRef(null);

    const [loading, setLoading] = useState(true);

    // Review Dialog States
    const toast = useRef(null);
    const [reviewDialog, setReviewDialog] = useState(false);
    const [selectedClaim, setSelectedClaim] = useState(null);
    const [reviewComment, setReviewComment] = useState('');
    const [reviewLoading, setReviewLoading] = useState(false);

    useMountEffect(() => {
        fetchAllClaims();
    });

    const fetchAllClaims = async () => {
        setLoading(true);
        try {
            const response = await getAllClaims();
            if (response.data && response.data.status === "SUCCESS") {
                const rawClaims = response.data.responseBody || [];
                const mappedClaims = rawClaims.map(c => ({
                    claimId: c.referenceNumber || c.id,
                    status: c.claimStatus || c.status,
                    policyNumber: c.policyNumber,
                    incidentDate: c.deceased?.dateOfDeath || 'N/A',
                    dateSubmitted: c.createdAt ? new Date(c.createdAt).toLocaleDateString() : 'N/A',
                    referenceNumber: c.referenceNumber
                }));
                setClaims(mappedClaims);
            } else {
                setClaims([]);
            }
        } catch (error) {
            console.error('Error fetching all claims:', error);
            setClaims([]);
        } finally {
            setLoading(false);
        }
    };

    const statusBodyTemplate = (rowData) => {
        const val = rowData.status ? rowData.status.toLowerCase() : '';
        let severity = 'warning';
        if (val.includes('approved') || val.includes('paid')) severity = 'success';
        if (val.includes('reject') || val.includes('denied')) severity = 'danger';
        return <Tag value={rowData.status} severity={severity}/>;
    };

    const openReviewDialog = (claim) => {
        setSelectedClaim(claim);
        setReviewComment('');
        setReviewDialog(true);
    };

    const closeReviewDialog = () => {
        setReviewDialog(false);
        setSelectedClaim(null);
    };

    const submitReview = async (approve) => {
        if (!selectedClaim) return;
        setReviewLoading(true);
        try {
            const res = await approveOrRejectClaim(selectedClaim.referenceNumber, approve, reviewComment);
            if (res.data && res.data.status === "SUCCESS") {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: res.data.message || 'Claim reviewed successfully',
                    life: 3000
                });
                closeReviewDialog();
                fetchAllClaims(); // refresh the list
            } else {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: res.data?.message || 'Failed to review claim',
                    life: 3000
                });
            }
        } catch (err) {
            console.error(err);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'An error occurred during review',
                life: 3000
            });
        } finally {
            setReviewLoading(false);
        }
    };


    const exportCSV = () => {
        dt.current.exportCSV();
    };

    const globalFilterElement = (
        <div className="relative w-full max-w-xs">
            <i className="pi pi-search absolute left-3 top-1/2 -translate-y-1/2 text-base-content/50"></i>
            <input
                type="text"
                className="input input-bordered w-full pl-10 h-10 shadow-sm bg-base-100 rounded-full"
                placeholder="Search..."
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
            />
        </div>
    );

    const header = (
        <div className="flex flex-col sm:flex-row gap-4 justify-between items-center w-full">
            {globalFilterElement}
            <div className="flex items-center gap-3 ml-auto">
                <div className="dropdown dropdown-end">
                    <div tabIndex={0} role="button"
                         className="btn btn-outline btn-ghost h-10 min-h-0 shadow-sm rounded-full">
                        <i className="pi pi-file-export"></i> Export
                    </div>
                    <ul tabIndex={0} className="dropdown-content z-[99] menu p-2 shadow-sm bg-base-100 border border-base-200 rounded-box w-52 mt-2">
                                    <li><a onClick={() => exportToCSV(dt, 'Claims')}><i className="pi pi-file-excel text-success"></i> Download CSV</a></li>
                                    <li><a onClick={() => exportToPDF(dt, 'Claims', 'Claims Report')}><i className="pi pi-file-pdf text-error"></i> Download PDF</a></li>
                                </ul>
                </div>
            </div>
        </div>
    );

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="actions-container flex items-center justify-center gap-2">
                <div className="tooltip" data-tip="View">
                    <button
                        className="btn btn-sm btn-circle btn-ghost text-base-content/70 hover:text-base-content hover:bg-base-200"
                        onClick={() => window.location.href = `/claims/view/${rowData.referenceNumber}`}
                    >
                        <i className="pi pi-eye text-lg"></i>
                    </button>
                </div>
                <div className="tooltip" data-tip="Review">
                    <button
                        className="btn btn-sm btn-circle btn-ghost text-base-content/70 hover:text-base-content hover:bg-base-200"
                        onClick={() => openReviewDialog(rowData)}
                    >
                        <i className="pi pi-file-edit text-lg"></i>
                    </button>
                </div>
            </div>
        );
    };

    const humanReadableDateBodyTemplate = (rowData, field) => {
        const dateVal = rowData[field];
        if (!dateVal) return '-';
        try {
            return new Date(dateVal).toLocaleString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            });
        } catch (e) {
            return dateVal;
        }
    };

    const reviewDialogFooter = (
        <React.Fragment>
            <Button label="Cancel" icon="pi pi-times" outlined onClick={closeReviewDialog} disabled={reviewLoading}/>
            <Button label="Reject" icon="pi pi-times-circle" severity="danger" onClick={() => submitReview(false)}
                    disabled={reviewLoading}/>
            <Button label="Approve" icon="pi pi-check" severity="success" onClick={() => submitReview(true)}
                    disabled={reviewLoading}/>
        </React.Fragment>
    );

    return (
        <>
            <Header/>
            <DynamicNavigation/>
            <Toast ref={toast}/>
            <div className="content-body dl-dashboard">
                <div className="w-full px-4">
                    <AppCard title="View All Claims">
                        <DataTable
                            ref={dt}
                            value={claims}
                            loading={loading}
                            emptyMessage="No claims found"
                            paginator
                            rows={10}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            paginatorTemplate="CurrentPageReport RowsPerPageDropdown PrevPageLink PageLinks NextPageLink"
                            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                            globalFilter={globalFilter}
                            header={header}
                        >
                            <Column field="claimId" header="Claim ID" sortable/>
                            <Column field="policyNumber" header="Policy Number" sortable/>
                            <Column field="incidentDate" header="Incident Date"
                                    body={(rowData) => humanReadableDateBodyTemplate(rowData, 'incidentDate')}
                                    sortable/>
                            <Column field="dateSubmitted" header="Date Submitted"
                                    body={(rowData) => humanReadableDateBodyTemplate(rowData, 'dateSubmitted')}
                                    sortable/>
                            <Column field="status" header="Status" body={statusBodyTemplate} sortable/>
                            <Column body={actionBodyTemplate} exportable={false} style={{minWidth: '10rem'}}></Column>
                        </DataTable>
                    </AppCard>
                </div>
            </div>

            <Dialog visible={reviewDialog} style={{width: '450px'}} header="Review Claim" modal className="p-fluid"
                    footer={reviewDialogFooter} onHide={closeReviewDialog}>
                {selectedClaim && (
                    <div className="field">
                        <label htmlFor="comment" className="font-bold mb-3 d-block">Comment / Reason</label>
                        <InputTextarea id="comment" value={reviewComment}
                                       onChange={(e) => setReviewComment(e.target.value)} required rows={4} autoResize/>
                        <small className="d-block mt-2">Please provide a reason for the approval or rejection.</small>
                    </div>
                )}
            </Dialog>
        </>
    );
};

export default AdminClaimsList;
